<template>
  <div class="latest-blog-post">
    <div v-if="loading" class="loading">
      <p>Loading latest post...</p>
    </div>
    
    <div v-else-if="error" class="error">
      <p>{{ error }}</p>
    </div>
    
    <div v-else-if="latestPost" class="post">
      <h2 class="post-title">{{ latestPost.title }}</h2>
      <div class="post-meta">
        <span class="post-date">{{ formatDate(latestPost.date) }}</span>
        <span v-if="latestPost.author" class="post-author">by {{ latestPost.author }}</span>
      </div>      
      <div class="post-excerpt-container">
        <div class="post-excerpt" v-html="latestPost.excerpt"></div>
      </div>
      
      <!-- changed: use external anchor to the original post URL instead of a router-link to a local slug -->
      <a :href="latestPost.link" class="read-more" target="_blank" rel="noopener noreferrer">
        Read More
      </a>
    </div>
    
    <div v-else class="no-posts">
      <p>No blog posts available.</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';

const latestPost = ref(null);
const loading = ref(true);
const error = ref(null);

// Format date
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  }).format(date);
};

// Constants for caching
const CACHE_KEY = 'latest-blog-post-cache';
const CACHE_EXPIRATION = 60 * 60 * 1000; // 1 hour in milliseconds

// changed: include the original WP post link so we can open the external post URL
const mapWordPressPost = (wpPost) => {
  return {
    title: wpPost.title.rendered,
    date: wpPost.date,
    author: wpPost._embedded?.author?.[0]?.name || null,
    featuredImage: wpPost._embedded?.['wp:featuredmedia']?.[0]?.source_url || null,
    excerpt: wpPost.excerpt.rendered,
    slug: wpPost.slug,
    link: wpPost.link // original external URL from WordPress
  };
};

// Fetch the latest blog post
const fetchLatestPost = async () => {
  loading.value = true;
  error.value = null;
  
  try {
    // Check for cached data first
    const cachedData = localStorage.getItem(CACHE_KEY);
    
    if (cachedData) {
      const { data, timestamp } = JSON.parse(cachedData);
      const now = new Date().getTime();
      
      // Use cached data if it's not expired
      if (now - timestamp < CACHE_EXPIRATION) {
        console.log('Using cached blog post data');
        if (data && data.length > 0) {
          latestPost.value = mapWordPressPost(data[0]);
        } else {
          latestPost.value = null;
        }
        loading.value = false;
        return;
      }
    }
    
    // Fetch from WordPress REST API if no valid cache exists
    const response = await fetch('https://buildthenbless.com/wp-json/wp/v2/posts?per_page=1&_embed');
    
    if (!response.ok) {
      throw new Error(`Error fetching blog posts: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // Cache the result
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      data,
      timestamp: new Date().getTime()
    }));
    
    // Process the WordPress post data
    if (data && data.length > 0) {
      latestPost.value = mapWordPressPost(data[0]);
    } else {
      latestPost.value = null;
    }
  } catch (err) {
    error.value = `Failed to load latest blog post: ${err.message}`;
    console.error(err);
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchLatestPost();
});
</script>

<style scoped>
.latest-blog-post {
  padding: 1.5rem;
  border-radius: 30px;
  background-color: #fff;
  border: solid 1px #D5D1C8;
  box-sizing: border-box;
}

/* make the post a column flex container so margin-top:auto works */
.post {
  display: flex;
  flex-direction: column;
  min-height: 260px; 
  height: 100%;
}

.loading, .error, .no-posts {
  padding: 2rem 0;
  text-align: center;
}

.error {
  color: #e74c3c;
}

.post-title {
  margin-top: 0;
  margin-bottom: 0.5rem;
  font-size: 1.8rem;
  color: #087DB5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
  max-height: calc(1.2em * 3); /* 3 lines */
}

.post-meta {
  margin-bottom: 1rem;
  color: #666;
  font-size: 0.9rem;
}

.post-author {
  margin-left: 0.5rem;
}

.post-excerpt-container {
  overflow: hidden;
  position: relative;
  margin-bottom: 1em;
  height: 120px;
  --fade-amount: 5%;
  mask-image: linear-gradient(
    black var(--fade-amount), 
    black calc(100% - var(--fade-amount)), 
    transparent
  );
  flex: 1;
}

.post-excerpt {
  position: relative;
  display: -webkit-box;
  -webkit-line-clamp: 7;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.6;
  color: #5F5E5B;
}

.read-more {
  display: inline-block;
  padding: 0.5rem 1rem;
  background-color: #087DB5;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-weight: bold;
  transition: background-color 0.2s;
  font-size: 1.2rem;
  width: fit-content;
  margin-top: auto; /* now works because .post is flex column */
}

.read-more:hover {
  background-color: #2980b9;
}
</style>